import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import type { StudyApiId } from '@/model/Study.model';
import { _notNil } from '@/littledash';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import APITable from '@/components/UI/Table/Reusable/APITable/APITable';
import { RouteService } from '@/support/RouteService';
import { web as webRoute } from '@/support/route';
import type { APITableAsyncExportCallback, AsyncExportRoute } from '@/model/AsyncExport.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useAsyncExport } from '@/support/Hooks/api/useAsyncExport';
import {
  auditLogColumns,
  auditLogFilterOptions,
  auditLogSearchByOptions,
  buildAuditLogExportBody,
  titleCaseEntityType,
} from '@/components/Studies/AuditLog/AuditLog.utils';
import type {
  AuditLogEntityHistory as AuditLogEntityHistoryResponse,
  AuditLogEntitySummary as EntitySummary,
  AuditLogRow,
} from '@/components/Studies/AuditLog/AuditLog.model';
import { AuditLogEntitySummary } from './AuditLogEntitySummary';
import {
  animalLinkLabelForEntityType,
  animalSubjectIdFromEntity,
  isLinkedAnimalDeleted,
  tabForEntityType,
} from './AuditLogEntityHistory.utils';

type EntityType = EntitySummary['entity_type'];

const ENTITY_FILTER_OPTIONS = auditLogFilterOptions({ scope: 'entity' });
const ENTITY_SEARCH_BY_OPTIONS = auditLogSearchByOptions({ scope: 'entity' });

export interface AuditLogEntityHistoryProps {
  studyApiId: StudyApiId;
  studyId: string;
  itemDeveloperId: string;
  entityType?: EntityType;
  closeModal: () => void;
}

const modalTitle = (entityType: EntityType | undefined): string => {
  if (!_notNil(entityType)) {
    return 'Entity audit log';
  }
  return `${titleCaseEntityType(entityType)} audit log`;
};

/**
 * Modal opened when the user clicks a row in the study audit log. Fetches the
 * entity summary (light shape from the /audit-logs/entities/{id} endpoint) for
 * the modal header, and re-uses the standard APITable to render the audit rows
 * scoped to that single entity.
 */
const AuditLogEntityHistory: FC<AuditLogEntityHistoryProps> = ({
  studyApiId,
  studyId,
  itemDeveloperId,
  entityType,
  closeModal,
}) => {
  const [summary, setSummary] = useState<EntitySummary | undefined>(undefined);
  const triggerAsyncExport = useAsyncExport();

  const { invoke: createAuditLogExport } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/exports/audit-logs',
    options: {
      invokeOnInit: false,
      onError: { toast: false, throw: false, slug: 'audit-log-entity-async-export' },
    },
  });

  const historyRoute = useMemo(
    () =>
      RouteService.api({
        endpoint: 'GET /api/v1/studies/{studyApiId}/audit-logs/entities/{itemDeveloperId}',
        path: { studyApiId, itemDeveloperId },
      }).url,
    [studyApiId, itemDeveloperId]
  );

  const columns = useMemo(() => auditLogColumns({ scope: 'entity' }), []);

  // APITable already fetches the /audit-logs/entities/{id} endpoint for its
  // rows; the wrapper response includes `entity` alongside `data`, so we grab
  // the entity summary from the same response instead of firing a second call.
  const captureEntitySummary = useCallback((_data: Array<AuditLogRow>, otherProps?: Record<string, unknown>) => {
    const nextEntity = (otherProps as AuditLogEntityHistoryResponse | undefined)?.entity;
    if (_notNil(nextEntity)) {
      setSummary(nextEntity);
    }
  }, []);

  // Two-entry dropdown mirroring the study audit-log page. Both routes stay scoped to this
  // entity via `item_developer_id` in the body; `noFilters` only decides whether the caller's
  // in-modal filter/search state travels with the export.
  const exportRoutes = useMemo<Array<AsyncExportRoute>>(
    () => [
      {
        endpoint: 'POST /api/v1/studies/{studyApiId}/exports/audit-logs',
        params: { studyApiId },
        label: 'All entries for this entity',
        noFilters: true,
      },
      {
        endpoint: 'POST /api/v1/studies/{studyApiId}/exports/audit-logs',
        params: { studyApiId },
        label: 'Current filter selection',
      },
    ],
    [studyApiId]
  );

  const handleAsyncExport = useCallback<APITableAsyncExportCallback>(
    async ({ route, view }) => {
      const body = buildAuditLogExportBody({ route, view, itemDeveloperId });
      await triggerAsyncExport(() => createAuditLogExport({ path: route.params, body }));
    },
    [createAuditLogExport, itemDeveloperId, triggerAsyncExport]
  );

  const resolvedEntityType = entityType ?? summary?.entity_type;
  const animalSubjectId = animalSubjectIdFromEntity(summary);
  const canNavigateToAnimal =
    _notNil(animalSubjectId) && !isLinkedAnimalDeleted(summary) && _notNil(resolvedEntityType);
  const animalHref = canNavigateToAnimal
    ? webRoute('animals.show.tab', {
        id: studyId,
        subject_id: animalSubjectId,
        tab: tabForEntityType(resolvedEntityType),
      })
    : undefined;
  const animalLinkLabel = animalLinkLabelForEntityType(resolvedEntityType);

  return (
    <ModalContainer size="wide" testKey="audit-log-entity-history-modal">
      <ModalHeader
        title={modalTitle(resolvedEntityType)}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
      />

      <div className="pa3" data-testid="audit-log-entity-history">
        {_notNil(summary) ? <AuditLogEntitySummary summary={summary} /> : null}

        <div className="mt3">
          <APITable<AuditLogRow>
            apiInfo={{ type: 'internalApi', route: historyRoute }}
            reduxTableName="entityAuditLog"
            columns={columns}
            filterOptions={ENTITY_FILTER_OPTIONS}
            searchQueryByOptions={ENTITY_SEARCH_BY_OPTIONS}
            searchPlaceholderText="Search"
            defaultSortBy={{ id: 'created_at', desc: true }}
            pageSizes={[25, 50, 100]}
            defaultPageSize={25}
            onFetchTableData={captureEntitySummary}
            testIdPrefix="audit-log-entity-history"
            hideRefreshButton
            exportRoutes={exportRoutes}
            onAsyncExport={handleAsyncExport}
          />
        </div>
      </div>

      <div className="pa3 bt b--moon-gray">
        <ModalActions
          onCancel={closeModal}
          cancelBtnText="Close"
          asideComponent={
            _notNil(animalHref) ? (
              <a
                className="ml-auto pl3 flex items-center min-h-100 link"
                href={animalHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="audit-log-entity-history__animal-link"
              >
                <p className="fw4 f5">{animalLinkLabel}</p>
              </a>
            ) : undefined
          }
        />
      </div>
    </ModalContainer>
  );
};

export default AuditLogEntityHistory;
